/**
 * Reconnect-backoff schedule — pure function, no timers, no I/O.
 *
 * Per CLIENT_DESIGN §3.7: "exponential backoff (1 s, 2 s, 4 s, 8 s, 16 s cap)".
 * First reconnect attempt (`attempt = 1`) waits 1 s; each subsequent attempt
 * doubles the delay up to the 16-s cap. `attempt = 0` is the initial
 * connection (no delay).
 *
 * The schedule is deliberately plain: predictable delays are easier to reason
 * about than jittered ones when a flaky Gateway could repeatedly yank every
 * connected client into the same backoff window. If that becomes a problem
 * at scale, add jitter here — the callers treat this as an opaque function.
 */

export const RECONNECT_CAP_MS = 16_000;

/**
 * Delay in milliseconds before `attempt`. `attempt = 0` is the initial
 * connection (returns 0); subsequent attempts follow 1s/2s/4s/8s/16s/16s/…
 */
export function reconnectDelayMs(attempt: number): number {
  if (attempt <= 0) return 0;
  const step = Math.pow(2, attempt - 1) * 1000;
  return Math.min(step, RECONNECT_CAP_MS);
}

/**
 * Classify a WebSocket close code to decide whether to retry.
 *
 * Per WIRE_PROTOCOL §4.2:
 *   - 1000  Normal — no retry (client initiated)
 *   - 4400  Protocol version mismatch — no retry (client needs reload)
 *   - 4401  Unauthorized — no retry (token bad)
 *   - 4403  Forbidden — no retry (kicked / banned)
 *   - 4408  Ping timeout — retry
 *   - 4409  Token in use — no retry (take-over requires existing session exit)
 *   - 4500  Server error — retry (transient)
 *   - 4503  Server shutting down — retry (gateway bounce)
 *   - other 4xxx — no retry (app-defined application-level reject)
 *   - transport-level / 1xxx-other — retry
 */
export function shouldRetry(code: number): boolean {
  switch (code) {
    case 1000: // Normal closure
    case 4400:
    case 4401:
    case 4403:
    case 4409:
      return false;
    case 4408:
    case 4500:
    case 4503:
      return true;
    default:
      // Unknown / transport-level codes: retry. The retry cap + cumulative
      // latency (1+2+4+8+16 ≈ 31s before flat-cap) gives the server time to
      // recover without permanently disconnecting a flapping client.
      return code < 4000; // 1001+ abnormal transport closure → retry
  }
}
